import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, Loader2, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginatedTableControls } from "@/components/ui/paginated-table";
import { SubmissionStatusPill } from "@/components/plan_review/SubmissionStatusPill";
import { SubmissionIntakeDialog } from "@/components/plan_review/SubmissionIntakeDialog";
import { useSubmissionsQueue } from "@/hooks/useSubmissionsQueue";
import {
  runSubmissionReview,
  type Submission,
  type SubmissionSort,
  type SubmissionStatus,
} from "@/api/submissions";
import { listPlanReviewChecklists, type PlanReviewChecklist } from "@/api/plan_review";
import { cn } from "@/lib/utils";

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "received", label: "Received" },
  { value: "in_review", label: "In review" },
  { value: "awaiting_revision", label: "Awaiting revision" },
  { value: "completed", label: "Completed" },
];

const SORT_OPTIONS: Array<{ value: SubmissionSort; label: string }> = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "status", label: "By status" },
  { value: "assignee", label: "By assignee" },
];

type SubmissionsDashboardProps = {
  municipalityId: number | null;
  onOpenReview: (planReviewId: number) => void;
  onNewReview: () => void;
};

export function SubmissionsDashboard({
  municipalityId,
  onOpenReview,
  onNewReview,
}: SubmissionsDashboardProps) {
  const queue = useSubmissionsQueue({ municipalityId: municipalityId ?? undefined });
  const [runReviewFor, setRunReviewFor] = useState<Submission | null>(null);

  const {
    submissions,
    planners,
    statusFilter,
    setStatusFilter,
    assigneeFilter,
    setAssigneeFilter,
    sort,
    setSort,
    search,
    setSearch,
    page,
    setPage,
    perPage,
    setPerPage,
    totalCount,
    totalPages,
    isLoading,
    error,
    assigning,
    assignPlanner,
    refresh,
  } = queue;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            Submitted plans for your municipality. Open one to run or review it.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubmissionIntakeDialog onCreated={() => refresh()} />
          <Button variant="link" className="h-auto px-2 text-sm" onClick={onNewReview}>
            Review a plan without logging
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={statusFilter}
          onValueChange={(value) =>
            setStatusFilter(value === "all" ? "all" : (value as SubmissionStatus))
          }
        >
          <SelectTrigger className="h-9 w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={assigneeFilter === "all" ? "all" : String(assigneeFilter)}
          onValueChange={(value) =>
            setAssigneeFilter(
              value === "all" ? "all" : value === "unassigned" ? "unassigned" : Number(value),
            )
          }
        >
          <SelectTrigger className="h-9 w-[180px]">
            <SelectValue placeholder="Assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {planners.map((planner) => (
              <SelectItem key={planner.id} value={String(planner.id)}>
                {planner.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sort} onValueChange={(value) => setSort(value as SubmissionSort)}>
          <SelectTrigger className="h-9 w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search project, applicant, address..."
          className="h-9 w-[240px]"
        />
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error instanceof Error &&
            (error.message.includes("403") || error.message.toLowerCase().includes("forbidden"))
            ? "Submissions aren't enabled for your team yet. You can still review a plan without logging one, or contact Ordinal support to turn on intake."
            : error instanceof Error
              ? error.message
              : "Failed to load submissions."}
        </div>
      ) : null}

      <div className="ordinal-scrollbar min-h-0 flex-1 overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[130px]">Status</TableHead>
              <TableHead>Project / Applicant</TableHead>
              <TableHead>Submitter</TableHead>
              <TableHead className="w-[110px]">Submitted</TableHead>
              <TableHead className="w-[200px]">Assigned</TableHead>
              <TableHead className="w-[140px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {submissions.map((submission) => (
              <TableRow key={submission.id}>
                <TableCell>
                  <SubmissionStatusPill status={submission.status} />
                </TableCell>
                <TableCell>
                  <div className="font-medium text-foreground">{submission.project_name}</div>
                  <div className="text-xs text-muted-foreground">{submission.applicant_name}</div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {submission.submitter_name ?? "—"}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {format(new Date(submission.created_at), "MMM d")}
                </TableCell>
                <TableCell>
                  <AssigneeCell
                    submission={submission}
                    planners={planners}
                    disabled={assigning}
                    onAssign={(plannerId) => assignPlanner(submission.id, plannerId)}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <SubmissionActionButton
                    submission={submission}
                    onOpenReview={onOpenReview}
                    onRunReview={() => setRunReviewFor(submission)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {submissions.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  No submissions yet.
                </TableCell>
              </TableRow>
            ) : null}
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      <PaginatedTableControls
        currentPage={page}
        totalPages={totalPages}
        perPage={perPage}
        totalCount={totalCount}
        variant="compact"
        onPageChange={setPage}
        onPerPageChange={setPerPage}
      />

      {runReviewFor ? (
        <RunReviewDialog
          submission={runReviewFor}
          municipalityId={municipalityId!}
          onClose={() => setRunReviewFor(null)}
          onStarted={(planReviewId) => {
            setRunReviewFor(null);
            refresh();
            onOpenReview(planReviewId);
          }}
        />
      ) : null}
    </div>
  );
}

function SubmissionActionButton({
  submission,
  onOpenReview,
  onRunReview,
}: {
  submission: Submission;
  onOpenReview: (planReviewId: number) => void;
  onRunReview: () => void;
}) {
  if (submission.status === "received") {
    return (
      <Button size="sm" onClick={onRunReview}>
        Run review
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      disabled={submission.latest_plan_review_id == null}
      onClick={() => {
        if (submission.latest_plan_review_id != null) onOpenReview(submission.latest_plan_review_id);
      }}
    >
      Open review
    </Button>
  );
}

function AssigneeCell({
  submission,
  planners,
  disabled,
  onAssign,
}: {
  submission: Submission;
  planners: Array<{ id: number; email: string }>;
  disabled: boolean;
  onAssign: (plannerId: number | null) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="h-8 max-w-full justify-start gap-1 px-2 font-normal"
        >
          {submission.assigned_planner_name ? (
            <span className="truncate">{submission.assigned_planner_name}</span>
          ) : (
            <span className="flex items-center gap-1 text-muted-foreground">
              <UserPlus className="h-3.5 w-3.5" /> Assign
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1">
        <div className="ordinal-scrollbar max-h-64 overflow-auto">
          {planners.map((planner) => (
            <button
              key={planner.id}
              type="button"
              className={cn(
                "flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                submission.assigned_planner_id === planner.id && "bg-accent font-medium",
              )}
              onClick={() => {
                onAssign(planner.id);
                setOpen(false);
              }}
            >
              <span className="truncate">{planner.email}</span>
            </button>
          ))}
          {submission.assigned_planner_id != null ? (
            <button
              type="button"
              className="mt-1 flex w-full items-center border-t px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
              onClick={() => {
                onAssign(null);
                setOpen(false);
              }}
            >
              Unassign
            </button>
          ) : null}
          {planners.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">No planners available.</div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RunReviewDialog({
  submission,
  municipalityId,
  onClose,
  onStarted,
}: {
  submission: Submission;
  municipalityId: number;
  onClose: () => void;
  onStarted: (planReviewId: number) => void;
}) {
  const [checklists, setChecklists] = useState<PlanReviewChecklist[]>([]);
  const [selectedChecklistId, setSelectedChecklistId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPlanReviewChecklists({ municipalityId, category: submission.plan_type })
      .then((list) => {
        if (cancelled) return;
        setChecklists(list);
        if (list.length > 0) setSelectedChecklistId(String(list[0].id));
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load checklists.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [municipalityId, submission.plan_type]);

  const handleStart = async () => {
    if (!selectedChecklistId) return;
    setStarting(true);
    setError(null);
    try {
      const { plan_review } = await runSubmissionReview(
        municipalityId,
        submission.id,
        Number(selectedChecklistId),
      );
      onStarted(plan_review.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to start review.");
      setStarting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Run AI review</DialogTitle>
          <DialogDescription>
            Choose a checklist to review “{submission.project_name}”.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading checklists...
          </div>
        ) : checklists.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            No checklists for this plan type yet. Create one from “New review”.
          </p>
        ) : (
          <Select value={selectedChecklistId} onValueChange={setSelectedChecklistId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a checklist" />
            </SelectTrigger>
            <SelectContent>
              {checklists.map((checklist) => (
                <SelectItem key={checklist.id} value={String(checklist.id)}>
                  {checklist.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={starting}>
            Cancel
          </Button>
          <Button onClick={handleStart} disabled={starting || loading || !selectedChecklistId}>
            {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Start review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
